import { apiSlice } from '../../app/api/apiSlice';

const ANIMATOR_URL = '/animator/';

export const animatorApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAnimatorResults: builder.query({
        query: () => '/game/animator-answers/', // Endpoint to get all answers
        providesTags: ['AnimatorResults'],
    }),
    getStrongExamples: builder.query({
        query: () => '/game/examples/strong/',
        providesTags: ['GameExample'],
    }),
    getMyAnimatedGameRuns: builder.query({
      query: () => `${ANIMATOR_URL}my-game-runs/`,
      providesTags: (result, error, arg) => {
        if (!result) return ['GameRun'];
        return [
          'GameRun',
          ...result.map(({ id }) => ({ type: 'GameRun', id }))
        ];
      }
    }),
    getGameRunAnswers: builder.query({
      query: (gameRunId) => `${ANIMATOR_URL}${gameRunId}/game-answers/`,
      providesTags: (result, error, gameRunId) => [
        { type: 'GameAnswer', id: gameRunId }
      ],
      transformResponse: (responseData) => {
        return responseData.grouped_answers || {};
      }
    }),
    getStrongGameExamples: builder.query({
      query: () => `${ANIMATOR_URL}strong-examples/`,
      providesTags: ['GameExample']
    }),
    getGameRunGroupDetails: builder.query({
      query: (gameRunId) => `${ANIMATOR_URL}${gameRunId}/groups/`,
      providesTags: (result, error, gameRunId) => [
        { type: 'GameRunGroup', id: gameRunId }
      ]
    }),
    // Player management endpoints (players are members with PLAYER role)
    // Use animator-specific endpoints
    getMyPlayers: builder.query({
      query: () => '/animator/players/',
      providesTags: (result = [], error, arg) => [
        'Player',
        ...result.map(({ id }) => ({ type: 'Player', id }))
      ],
    }),
    createPlayer: builder.mutation({
      query: playerData => ({
        url: '/animator/players/create/',
        method: 'POST',
        body: playerData,
      }),
      invalidatesTags: ['Player', 'GameRun', 'Group'],
    }),
    updatePlayer: builder.mutation({
      query: ({ id, data }) => ({
        url: `/animator/players/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => ['Player', { type: 'Player', id: arg.id }, 'GameRun', 'Group'],
    }),
    deletePlayer: builder.mutation({
      query: ({ id }) => ({
        url: `/animator/players/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => ['Player', { type: 'Player', id: arg.id }, 'GameRun', 'Group'],
    }),
    // Game run management for animators
    animatorSetGameRunStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/game/runs/${id}/set-status/`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameRun', id: arg.id }, 'GameRun'],
    }),
    animatorDeleteGameRun: builder.mutation({
      query: ({ id }) => ({
        url: `/game/runs/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameRun', id: arg.id }, 'GameRun'],
    }),
    // Get players status for a specific game run
    getGameRunPlayersStatus: builder.query({
      query: (gameRunId) => `${ANIMATOR_URL}${gameRunId}/players-status/`,
      providesTags: (result, error, gameRunId) => [
        { type: 'GameRunPlayers', id: gameRunId },
        ...(result || []).map(({ id }) => ({ type: 'PlayerStatus', id }))
      ]
    }),
    // Set player pause status
    setPlayerPauseStatus: builder.mutation({
      query: ({ gameRunId, playerId, isPaused }) => ({
        url: `${ANIMATOR_URL}${gameRunId}/set-player-pause/`,
        method: 'POST',
        body: { player_id: playerId, is_paused: isPaused },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'GameRunPlayers', id: arg.gameRunId },
        { type: 'PlayerStatus', id: arg.playerId }
      ],
    }),
    // Get game run progress
    getGameRunProgress: builder.query({
      query: (gameRunId) => `${ANIMATOR_URL}${gameRunId}/progress/`,
      providesTags: (result, error, gameRunId) => [
        { type: 'GameRunProgress', id: gameRunId }
      ]
    }),
    // Create groups for a game run
    createGroups: builder.mutation({
      query: ({ gameRunId, groups }) => ({
        url: `${ANIMATOR_URL}${gameRunId}/create-groups/`,
        method: 'POST',
        body: { groups },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'GameRun', id: arg.gameRunId },
        { type: 'GameRunGroup', id: arg.gameRunId },
        'GameRun'
      ],
    }),
    // Update game run catchup setting
    updateGameRunCatchup: builder.mutation({
      query: ({ gameRunId, allowCatchup }) => ({
        url: `/game/runs/${gameRunId}/`,
        method: 'PATCH',
        body: { allow_catchup: allowCatchup },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'GameRun', id: arg.gameRunId },
        'GameRun'
      ],
    }),
    // Assign player to a group (or remove from group if groupId is null)
    assignPlayerToGroup: builder.mutation({
      query: ({ playerId, groupId, gameRunId }) => ({
        url: `${ANIMATOR_URL}assign-player-to-group/`,
        method: 'POST',
        body: { player_id: playerId, group_id: groupId, game_run_id: gameRunId },
      }),
      invalidatesTags: (result, error, arg) => [
        'Player',
        { type: 'Player', id: arg.playerId },
        { type: 'GameRunGroup', id: arg.gameRunId },
        'GameRun'
      ],
    }),
    // Get all groups for animator (across all game runs)
    getAllGroups: builder.query({
      query: () => `${ANIMATOR_URL}all-groups/`,
      providesTags: (result = [], error, arg) => [
        'Group',
        ...result.map(({ id }) => ({ type: 'Group', id }))
      ],
    }),
    // AI Analysis of misalignments
    performAIAnalysis: builder.mutation({
      query: ({ gameRunId, groupId }) => ({
        url: `${ANIMATOR_URL}${gameRunId}/ai-analysis/`,
        method: 'POST',
        body: { group_id: groupId },
      }),
    }),
  })
});

export const {
  useGetAnimatorResultsQuery,
  useGetStrongExamplesQuery,
  useGetMyAnimatedGameRunsQuery,
  useGetGameRunAnswersQuery,
  useGetStrongGameExamplesQuery,
  useGetGameRunGroupDetailsQuery,
  useGetMyPlayersQuery,
  useCreatePlayerMutation,
  useUpdatePlayerMutation,
  useDeletePlayerMutation,
  useAnimatorSetGameRunStatusMutation,
  useAnimatorDeleteGameRunMutation,
  useGetGameRunPlayersStatusQuery,
  useSetPlayerPauseStatusMutation,
  useGetGameRunProgressQuery,
  useCreateGroupsMutation,
  useUpdateGameRunCatchupMutation,
  useAssignPlayerToGroupMutation,
  useGetAllGroupsQuery,
  usePerformAIAnalysisMutation,
} = animatorApiSlice;
